import type { NumberLocale } from "../core/parser/locale";
import { parseAmount, parseDate } from "../core/parser/locale";
import type { BalanceInfo, BankTemplate, ColumnLayout } from "../core/parser/template";
import { textInXRange } from "../core/pdf/lines";
import type { TextLine } from "../core/types";

// ALTA Banka a.d. Beograd — RSD current-account statement ("Izvod").
// Reverse-engineered from a real statement.
//
// Layout (PDF points): R.b. | Datum valute | Datum knjiženja | Opis |
//   Priliv (in) | Odliv (out, printed with a leading minus) | Stanje | Datum
//   transakcije | Referentna oznaka.
// Numbers are dot-decimal ("992.78"); the opening balance can be negative
// ("Početno stanje : -330.45"). A zero-movement row (e.g. "Izvod tekuceg
// racuna") leaves Priliv/Odliv blank rather than "0.00" — kept via
// missingAmountIsZero. No explicit closing total: the last row's running
// balance is the closing balance.

export const ALTA_RS_LOCALE: NumberLocale = {
  decimalSeparator: ".",
  thousandsSeparator: ",",
};

export const ALTA_RS_COLUMNS: ColumnLayout = {
  valueDate: { xMin: 25, xMax: 75 }, // Datum valute
  date: { xMin: 75, xMax: 118 }, // Datum knjiženja (booking date)
  description: { xMin: 118, xMax: 255 }, // Opis
  credit: { xMin: 255, xMax: 320 }, // Priliv (money in)
  debit: { xMin: 320, xMax: 395 }, // Odliv (money out, already signed)
  balance: { xMin: 395, xMax: 448 }, // Stanje (running balance)
};

const DATE_CELL = /^\d{2}\.\d{2}\.\d{4}$/;

export const altaBankaRsTemplate: BankTemplate = {
  id: "rs-alta",
  name: "ALTA Banka a.d. Beograd",
  country: "RS",
  currency: "RSD",
  numberLocale: ALTA_RS_LOCALE,
  dateFormats: ["DD.MM.YYYY"],
  layout: ALTA_RS_COLUMNS,
  dateCellPattern: DATE_CELL,
  missingAmountIsZero: true,
  stopRowPattern:
    /ALTA Banka|Mati[čc]ni broj|Tel:|www\.|Datum valute|Datum knji|R\.b\.|Priliv|Odliv|Referentna|Ukupno|Po[cč]etno stanje|Kamatna|Dozvoljena|Swift/i,

  detect(fullText: string): number {
    const t = fullText.toLowerCase();
    let score = 0;
    if (t.includes("alta banka")) score += 0.6;
    if (t.includes("altabanka")) score += 0.3;
    if (t.includes("izvod broj")) score += 0.1;
    return Math.min(score, 1);
  },

  extractBalances(lines: TextLine[], locale: NumberLocale): BalanceInfo {
    const full = lines.map((l) => l.text).join("\n");

    const opening = matchAmount(full, /po[cč]etno stanje\s*:?\s*(-?[\d.,]+)/i, locale);
    // No explicit closing line — use the last transaction row's running balance.
    const closing = lastRowBalance(lines, locale);

    const period =
      /u periodu\s*:?\s*(\d{2}\.\d{2}\.\d{4})\s*-\s*(\d{2}\.\d{2}\.\d{4})/i.exec(full);
    const accMatch = /broj ra[cč]una\s*:?\s*([\d\s-]+)/i.exec(full);
    const account = accMatch
      ? accMatch[1]!.replace(/\s+/g, "").replace(/-+$/, "") || undefined
      : undefined;

    return {
      opening,
      closing,
      periodStart: period ? parseDate(period[1]!, ["DD.MM.YYYY"]) ?? undefined : undefined,
      periodEnd: period ? parseDate(period[2]!, ["DD.MM.YYYY"]) ?? undefined : undefined,
      accountId: account,
    };
  },
};

/** Running balance of the last transaction row — the de-facto closing balance. */
function lastRowBalance(lines: TextLine[], locale: NumberLocale): number | null {
  const B = ALTA_RS_COLUMNS.balance!;
  const D = ALTA_RS_COLUMNS.date;
  let last: number | null = null;
  for (const line of lines) {
    if (!DATE_CELL.test(textInXRange(line, D.xMin, D.xMax))) continue;
    const bal = parseAmount(textInXRange(line, B.xMin, B.xMax), locale);
    if (bal != null) last = bal;
  }
  return last;
}

function matchAmount(text: string, re: RegExp, locale: NumberLocale): number | null {
  const m = re.exec(text);
  if (!m || !m[1]) return null;
  return parseAmount(m[1], locale);
}
